// Graphics quality settings — a small Low / Medium / High preset system over the
// render knobs that actually move the needle on a weak GPU: pixel ratio, bloom,
// and sun-shadow map size + update cadence. The chosen level persists in
// localStorage. render.ts is the single consumer — it subscribes via
// onQualityChange and applies each change to the live renderer + sun shadow.
//
// Why THESE knobs: bloom is the most expensive per-pixel effect, and pixel ratio
// scales the whole framebuffer quadratically, so dropping both roughly doubles
// FPS on an integrated GPU. Shadow-map size is a lag-free 4× cut (2048 → 1024);
// shadows-off is the last resort on "low". Default is "high" — we never silently
// degrade anyone; the menu (press O) is the opt-in lever, F3 shows the payoff.

export type QualityLevel = "low" | "medium" | "high";

export type GraphicsSettings = {
  /** Cap on the render pixel ratio; effective = min(devicePixelRatio, this). */
  pixelRatio: number;
  /** Bloom post-pass on/off (the priciest per-pixel effect). */
  bloom: boolean;
  /** Sun shadow-map resolution in px; 0 disables sun shadows entirely. */
  shadowMapSize: number;
  /** Frames between sun shadow re-renders (1 = every frame). Kept small on
   *  purpose: the sun frustum follows the player, so a large interval makes
   *  shadows visibly lag when running. */
  shadowInterval: number;
  /** Toon silhouette-outline post pass (plan 23) — one cheap 5-tap fullscreen
   *  pass. OFF in every preset by default (user preference — the depth-outline
   *  read wasn't wanted); the pass stays wired + F6-tunable, flip a preset here
   *  or toggle it live to re-enable. */
  outline: boolean;
  /** SMAA anti-aliasing post pass (the composer bypasses MSAA entirely, so
   *  without this there is NO AA — but it's the priciest of the new passes).
   *  With bloom/outline/smaa ALL off (Low), render.ts skips the composer and
   *  renders direct, which brings the canvas's native MSAA back to life. */
  smaa: boolean;
  /** How many of the nearest night lamps cast (statically baked) cube shadow
   *  maps. Each one costs a cube-map sample per lamp-lit pixel per frame, and
   *  a 6-pass bake whenever it moves to a new spot. The rest of the lamp pool
   *  runs unshadowed (light bleeds through walls at range — the tradeoff Low
   *  already accepts for the sun). */
  lampShadows: number;
  /** Rim/fresnel edge light (toon.ts toonify). Off = compiled OUT of every toon
   *  program (the pow/dot ALU otherwise runs on every toon fragment even at
   *  strength 0), so Low sheds per-pixel shader cost at the price of losing the
   *  edge-sheen highlight. */
  rim: boolean;
};

export const QUALITY_PRESETS: Record<QualityLevel, GraphicsSettings> = {
  low:    { pixelRatio: 1,   bloom: false, shadowMapSize: 0,    shadowInterval: 1, outline: false, smaa: false, lampShadows: 0, rim: false },
  medium: { pixelRatio: 1.5, bloom: true,  shadowMapSize: 1024, shadowInterval: 2, outline: false, smaa: false, lampShadows: 2, rim: true },
  high:   { pixelRatio: 2,   bloom: true,  shadowMapSize: 2048, shadowInterval: 1, outline: false, smaa: true,  lampShadows: 4, rim: true },
};

const LEVELS: QualityLevel[] = ["low", "medium", "high"];
const STORAGE_KEY = "gfxQuality";

function loadLevel(): QualityLevel {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "low" || v === "medium" || v === "high") return v;
  } catch { /* private mode / storage blocked — fall through to default */ }
  return "medium";
}

let level: QualityLevel = loadLevel();
const subs = new Set<(s: GraphicsSettings, level: QualityLevel) => void>();

export function quality(): QualityLevel { return level; }
export function settings(): GraphicsSettings { return QUALITY_PRESETS[level]; }

export function setQuality(next: QualityLevel): void {
  if (next === level) return;
  level = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  for (const fn of subs) fn(QUALITY_PRESETS[level], level);
}

/** Subscribe to quality changes. Fires IMMEDIATELY with the current settings so
 *  the consumer can do its initial apply in the same place it handles updates.
 *  Subscribers live for the page lifetime (no unsubscribe needed). */
export function onQualityChange(
  fn: (s: GraphicsSettings, level: QualityLevel) => void,
): void {
  subs.add(fn);
  fn(QUALITY_PRESETS[level], level);
}

// ---- Reusable Low/Med/High selector -----------------------------------------

/** Build a self-contained Low/Med/High button row. Clicking a button sets the
 *  active quality; the row highlights the active level and stays in sync via
 *  onQualityChange, so multiple instances (the in-world O-panel + the
 *  character-select Settings view) can't disagree. Instances live for the page
 *  lifetime (onQualityChange has no unsubscribe — matching its contract). */
export function makeGraphicsControls(): HTMLElement {
  const row = document.createElement("div");
  row.style.cssText = "display:flex; gap:6px;";
  const btns: Partial<Record<QualityLevel, HTMLButtonElement>> = {};
  for (const L of LEVELS) {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = L[0]!.toUpperCase() + L.slice(1);
    b.style.cssText =
      "flex:1; cursor:pointer; padding:5px 0; border-radius:6px;" +
      "font:12px ui-monospace, monospace; border:1px solid #3a4a3a;" +
      "background:#1a241a; color:#cfe3cf;";
    b.onclick = () => setQuality(L);
    btns[L] = b;
    row.appendChild(b);
  }
  onQualityChange((_s, active) => {
    for (const L of LEVELS) {
      const b = btns[L];
      if (!b) continue;
      const on = L === active;
      b.style.background = on ? "#2f6d3f" : "#1a241a";
      b.style.borderColor = on ? "#4a9a5a" : "#3a4a3a";
      b.style.color = on ? "#eafff0" : "#cfe3cf";
    }
  });
  return row;
}

// ---- Tiny settings panel (no HUD button — toggled by the O key) --------------

let panel: HTMLDivElement | null = null;

export function initSettingsUI(): void {
  panel = document.createElement("div");
  panel.id = "gfx-panel";
  panel.style.cssText =
    "position:fixed; top:196px; right:16px; z-index:8; display:none;" +
    "font:12px/1.5 ui-monospace, Menlo, monospace; color:#cfe3cf;" +
    "background:rgba(12,16,12,.9); border:1px solid #3a4a3a; border-radius:8px;" +
    "padding:10px 12px; min-width:176px;";

  const title = document.createElement("div");
  title.textContent = "Graphics quality";
  title.style.cssText = "margin-bottom:8px; color:#9fd3ff; letter-spacing:.03em;";
  panel.appendChild(title);

  panel.appendChild(makeGraphicsControls());

  const hint = document.createElement("div");
  hint.style.cssText = "margin-top:8px; color:#7a8a7a; font-size:11px; max-width:200px;";
  hint.textContent = "Lower = smoother on weak GPUs. Press F3 for FPS + GPU.";
  panel.appendChild(hint);

  document.body.appendChild(panel);
}

export function toggleSettings(): void {
  if (!panel) return;
  panel.style.display = panel.style.display === "none" ? "block" : "none";
}
